import { Moon, Sun } from "lucide-react";

import { cn } from "~/lib/utils";
import { useResolvedTheme, useThemeStore } from "~/modules/Theme/themeStore";

type ThemeSwitchProps = {
  /** `on-dark` is for a footer or another surface that never becomes light. */
  tone?: "adaptive" | "on-dark";
  label?: string;
  showStatus?: boolean;
  className?: string;
  labelClassName?: string;
  controlClassName?: string;
};

/**
 * A route-independent light/dark switch. It resolves the "system" preference to
 * the theme currently shown, then stores the user's explicit choice on interaction.
 */
export function ThemeSwitch({
  tone = "adaptive",
  label = "Modo oscuro",
  showStatus = false,
  className,
  labelClassName,
  controlClassName,
}: ThemeSwitchProps) {
  const resolvedTheme = useResolvedTheme();
  const setTheme = useThemeStore((state) => state.setTheme);
  const isDark = resolvedTheme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={`${label}: ${isDark ? "activado" : "desactivado"}`}
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={cn(
        "group inline-flex min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2 font-landing transition-[background-color,border-color,color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-red-400",
        tone === "on-dark"
          ? "border-landing-on-dark/15 bg-landing-on-dark/[0.06] text-landing-on-dark hover:border-landing-on-dark/25 hover:bg-landing-on-dark/[0.1]"
          : "border-landing-stone-300 bg-landing-white text-landing-stone-800 hover:border-landing-stone-400 hover:bg-landing-stone-100",
        className,
      )}
    >
      {isDark ? (
        <Moon className="size-[17px] shrink-0" aria-hidden="true" />
      ) : (
        <Sun className="size-[17px] shrink-0" aria-hidden="true" />
      )}
      <span className={cn("min-w-0 flex-1 text-left text-[13.5px] font-semibold", labelClassName)}>
        {label}
      </span>
      {showStatus && (
        <span
          className={cn(
            "hidden text-[11px] font-medium sm:inline",
            tone === "on-dark" ? "text-landing-on-dark/60" : "text-landing-stone-500",
          )}
        >
          {isDark ? "Sí" : "No"}
        </span>
      )}
      <span
        aria-hidden="true"
        className={cn(
          "relative h-[22px] w-10 shrink-0 rounded-full border transition-colors duration-200",
          isDark
            ? "border-landing-red-400 bg-landing-red-400"
            : tone === "on-dark"
              ? "border-landing-on-dark/25 bg-landing-on-dark/10"
              : "border-landing-stone-400 bg-landing-stone-200",
          controlClassName,
        )}
      >
        {/* `left-[3px]` explícito, no `translate-x` a secas: el knob es `absolute`
            dentro de un <span> inline, así que sin `left` arrancaba desde su posición
            estática (que no es el borde izquierdo del track) y el desplazamiento
            quedaba corrido. Medido: apagado el knob caía a la DERECHA (23px de hueco
            a la izquierda) y encendido se salía del track (−14px por fuera).
            Track 40px con borde de 1 y knob de 14 → 3px de inset a cada lado y
            recorrido de 18px deja los dos estados simétricos. */}
        <span
          className={cn(
            "absolute left-[3px] top-[3px] size-3.5 rounded-full shadow-sm transition-transform duration-200 ease-out",
            isDark ? "translate-x-[18px]" : "translate-x-0",
            tone === "on-dark" || isDark ? "bg-landing-on-dark" : "bg-landing-stone-600",
          )}
        />
      </span>
    </button>
  );
}
